import { useCallback, useEffect, useState } from 'react'

// Persist a prompt card's dismiss / hide(collapse) choice locally so it survives refresh + the
// live re-detect that re-emits the same prompt every frame. One active prompt per chat, so we
// keep a SINGLE record per chat (bounded — no growing set). See InteractivePromptCard.

export interface PromptHideRec { key: string; mode: 'dismissed' | 'hidden' }

// Stable identity across cursor navigation: the backend prompt id embeds `sel=` for single-
// select (changes as the ❯ moves), so a dismiss keyed on it would slip. Question + option
// labels don't change while the same prompt is up → use them as the persistence key.
export function stablePromptKey(p: { question: string; options: { label: string }[] }): string {
  return p.question + '|' + p.options.map((o) => o.label).join('|')
}

const storeKey = (chatId: string) => `whimer.prompt.${chatId}`

function read(chatId: string): PromptHideRec | null {
  try { return JSON.parse(localStorage.getItem(storeKey(chatId)) || 'null') } catch { return null }
}

export function usePromptCardHide(chatId: string) {
  const [rec, setRec] = useState<PromptHideRec | null>(() => read(chatId))
  useEffect(() => { setRec(read(chatId)) }, [chatId])
  const set = useCallback((r: PromptHideRec | null) => {
    try {
      if (r) localStorage.setItem(storeKey(chatId), JSON.stringify(r))
      else localStorage.removeItem(storeKey(chatId))
    } catch { /* localStorage unavailable — in-memory only */ }
    setRec(r)
  }, [chatId])
  return { rec, set }
}
